import { Link } from "react-router-dom";
import {
  Zap,
  Layers,
  Timer,
  Compass,
  MessageSquareQuote,
  Briefcase,
  Mail,
  Building2,
  Newspaper,
  type LucideIcon,
} from "lucide-react";

export type RailItem = {
  key: string;
  href: string;
  label: string;
};

const ICONS: Record<string, LucideIcon> = {
  advantage: Zap,
  services: Layers,
  sprint: Timer,
  principles: Compass,
  reviews: MessageSquareQuote,
  careers: Briefcase,
  contact: Mail,
  rpo: Building2,
  insights: Newspaper,
};

type Props = {
  items: readonly RailItem[];
  activeKey?: string | null;
  expanded: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onNavigate: (key: string) => void;
  rpoLabel: string;
  insightsLabel: string;
};

const itemBase =
  "group relative flex items-center h-12 w-full border border-transparent transition-all duration-200 outline-none focus-visible:border-lime-300/70";

function ItemInner({
  Icon,
  label,
  expanded,
  active,
}: {
  Icon: LucideIcon;
  label: string;
  expanded: boolean;
  active: boolean;
}) {
  return (
    <>
      <span
        className={`absolute left-0 top-0 bottom-0 w-[2px] transition-all duration-200 ${
          active ? "bg-lime-300" : "bg-transparent group-hover:bg-lime-300/40"
        }`}
        aria-hidden
      />
      <span className="flex items-center justify-center w-[62px] shrink-0">
        <Icon
          size={20}
          strokeWidth={1.75}
          className={`transition-colors duration-200 ${
            active ? "text-lime-300" : "text-white/60 group-hover:text-lime-300"
          }`}
        />
      </span>
      <span
        className={`text-[11px] uppercase tracking-[0.22em] whitespace-nowrap transition-all duration-200 ${
          expanded ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2 pointer-events-none"
        } ${active ? "text-lime-300" : "text-white/70 group-hover:text-white"}`}
      >
        {label}
      </span>
    </>
  );
}

export default function SideRailNav({
  items,
  activeKey,
  expanded,
  onMouseEnter,
  onMouseLeave,
  onNavigate,
  rpoLabel,
  insightsLabel,
}: Props) {
  return (
    <aside
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`hidden lg:flex fixed left-0 top-1/2 -translate-y-1/2 z-40 flex-col overflow-hidden border border-white/10 border-l-0 bg-[#050706]/95 backdrop-blur-md transition-[width] duration-300 ease-out ${
        expanded ? "w-[236px]" : "w-[62px]"
      }`}
      style={{ boxShadow: "8px 0 40px -24px rgba(189,255,0,0.35)" }}
      aria-label="Nawigacja sekcji"
    >
      <div className="h-12 border-b border-b-white/10" aria-hidden />

      <nav className="flex flex-col py-2">
        {items.map((n) => {
          const Icon = ICONS[n.key] ?? Zap;
          const active = activeKey === n.key;
          return (
            <a
              key={n.key}
              href={n.href}
              onClick={(e) => {
                e.preventDefault();
                onNavigate(n.key);
              }}
              aria-current={active ? "true" : undefined}
              title={expanded ? undefined : n.label}
              className={`${itemBase} ${active ? "bg-lime-300/[0.07]" : "hover:bg-white/[0.04]"}`}
            >
              <ItemInner Icon={Icon} label={n.label} expanded={expanded} active={active} />
            </a>
          );
        })}
      </nav>

      <div className="mt-auto flex flex-col border-t border-white/10 py-2">
        <Link
          to="/services/it-rpo-poland"
          title={expanded ? undefined : rpoLabel}
          className={`${itemBase} hover:bg-white/[0.04]`}
        >
          <ItemInner Icon={ICONS.rpo} label={rpoLabel} expanded={expanded} active={false} />
        </Link>
        <Link
          to="/insights"
          title={expanded ? undefined : insightsLabel}
          className={`${itemBase} hover:bg-white/[0.04]`}
        >
          <ItemInner Icon={ICONS.insights} label={insightsLabel} expanded={expanded} active={false} />
        </Link>
      </div>
    </aside>
  );
}
